import { ReactNode } from 'react';
import { AdminPage, AdminPageProps } from './AdminPage';
import { OtsDataTableScrollContext } from './OtsDataTable';
import { OtsPagination, OtsPaginationProps } from './OtsPagination';

export type OtsTablePanelProps = {
  title?: ReactNode;
  /** Usually a count, e.g. "137 process runs". */
  description?: ReactNode;
  /** Header-right buttons, e.g. "Clear filters". */
  actions?: ReactNode;
  /** A row under the header for status tabs, a search box, or column toggles. */
  toolbar?: ReactNode;
  /** Footer content; when `pagination` is also set it renders beside the pager. */
  footer?: ReactNode;
  /** Renders an OtsPagination in the footer. */
  pagination?: OtsPaginationProps;
  compact?: boolean;
  /** Fills the remaining height of a fill-layout page, with the body scrolling. */
  fill?: boolean;
  className?: string;
  ariaLabel?: string;
  /** The table, or an empty / loading state. */
  children: ReactNode;
};

/**
 * Header, scrolling table body, and footer in one bordered panel. Tables inside drop their own
 * border and scroll wrapper so the header row sticks while the body scrolls.
 */
export const OtsTablePanel = ({
  title,
  description,
  actions,
  toolbar,
  footer,
  pagination,
  compact,
  fill = true,
  className,
  ariaLabel,
  children,
}: OtsTablePanelProps) => {
  const pad = compact ? 'px-3' : 'px-4 sm:px-5';
  const hasHeader = title != null || description != null || actions != null;
  return (
    <section
      aria-label={ariaLabel}
      className={[
        'flex min-w-0 flex-col overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]',
        fill ? 'min-h-[18rem] flex-1' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {hasHeader && (
        <div
          className={[
            'flex shrink-0 flex-wrap items-start justify-between gap-3 border-b border-[var(--ots-outline-variant)]',
            pad,
            compact ? 'py-2.5' : 'py-3.5',
          ].join(' ')}
        >
          <div className="min-w-0">
            {title != null && (
              <h2
                className={[
                  'font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]',
                  compact ? 'text-sm' : 'text-base',
                ].join(' ')}
              >
                {title}
              </h2>
            )}
            {description != null && (
              <p className={['text-[var(--ots-text-muted)]', compact ? 'text-xs' : 'mt-0.5 text-sm'].join(' ')}>
                {description}
              </p>
            )}
          </div>
          {actions != null && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {toolbar != null && (
        <div
          className={[
            'flex shrink-0 flex-wrap items-center gap-3 border-b border-[var(--ots-outline-variant)]',
            pad,
            compact ? 'py-2' : 'py-3',
          ].join(' ')}
        >
          {toolbar}
        </div>
      )}
      <div className={['min-h-0 min-w-0', fill ? 'flex-1 overflow-auto' : 'overflow-x-auto'].join(' ')}>
        <OtsDataTableScrollContext.Provider value={fill}>{children}</OtsDataTableScrollContext.Provider>
      </div>
      {(footer != null || pagination) && (
        <div
          className={[
            'flex shrink-0 flex-wrap items-center gap-3 border-t border-[var(--ots-outline-variant)]',
            pad,
            compact ? 'py-2' : 'py-3',
          ].join(' ')}
        >
          {footer != null && <div className="min-w-0 text-xs text-[var(--ots-text-muted)]">{footer}</div>}
          {pagination && <OtsPagination compact={compact} {...pagination} className="ml-auto min-w-0 flex-1" />}
        </div>
      )}
    </section>
  );
};

export type OtsTableScreenProps = Omit<AdminPageProps, 'layout' | 'children' | 'fullWidth'> & {
  /** Fixed content above the table: alerts, filters, search forms. */
  above?: ReactNode;
  /** The OtsTablePanel (or a view that renders one). */
  children: ReactNode;
  compact?: boolean;
};

/**
 * The standard list screen: page header, fixed filters, then a table panel filling the rest of
 * the height. When the host is too short, the page scrolls instead of crushing the table.
 */
export const OtsTableScreen = ({ above, children, compact, className, ...page }: OtsTableScreenProps) => (
  <AdminPage
    {...page}
    fullWidth
    layout="fill"
    density={compact ? 'compact' : page.density ?? 'default'}
    className={['!overflow-y-auto', className].filter(Boolean).join(' ')}
  >
    <div className={['flex min-h-0 flex-1 flex-col', compact ? 'mt-3 gap-3' : 'mt-6 gap-4'].join(' ')}>
      {above != null && <div className={['shrink-0', compact ? 'space-y-3' : 'space-y-4'].join(' ')}>{above}</div>}
      {children}
    </div>
  </AdminPage>
);
